/**
 * Photo quotes: a customer sends photos of their home and gets a price back.
 *
 * The vocabulary and every decision that is arithmetic rather than judgment —
 * the coverage check, the risk score, the decision it leads to, the SLA clock
 * and the width of a price range — live here, shared by the Worker that runs
 * the pipeline and the web app that shows the review screen, so the badge the
 * owner sees and the rule the server applied can never disagree.
 *
 * The same rule as every AI assist holds: the model proposes structure, the
 * company's own library and the pricing engine produce every number, and a
 * person sends the price. Nothing in this version sends one automatically; the
 * `auto_firm` branch of the decision is implemented and tested so it can be
 * switched on later, and is fed `enabled: false` until then.
 */

import { z } from 'zod';
import {
  ROOM_CONDITION_CODES,
  ROOM_SIZE_CODES,
  type PlanId,
  type RoomConditionCode,
  type RoomSizeCode,
} from './constants/index.ts';
import type { SupportedLanguage } from './constants/languages.ts';
import { addDaysToDate, dayOfWeekOf, localDateTimeToUtc } from './calculations/recurrence.ts';
import { windowMinutes, type WorkingHours } from './scheduling-hours.ts';

// ---------------------------------------------------------------------------
// Statuses and vocabularies
// ---------------------------------------------------------------------------

/**
 * Where one batch of photos is.
 *
 *  - `collecting` — the upload page made it; the customer is still adding photos.
 *  - `analyzing` — submitted on a plan that reads photos; the pipeline is working.
 *  - `awaiting_owner` — waiting for a person. On a plan without photo reading
 *    this is where a submission lands straight away (as a request in the inbox).
 *  - `needs_more_photos` — the office asked the customer for more.
 *  - `sent` — a price went out (the quote it created carries the rest).
 *  - `closed` — handled another way: a walkthrough, or the request was converted
 *    or dismissed from the inbox.
 *  - `expired` — the customer never finished uploading.
 */
export const PHOTO_INTAKE_STATUSES = [
  'collecting',
  'analyzing',
  'awaiting_owner',
  'needs_more_photos',
  'sent',
  'closed',
  'expired',
] as const;
export type PhotoIntakeStatus = (typeof PHOTO_INTAKE_STATUSES)[number];

/** The statuses that belong in the owner's queue. */
export const OPEN_PHOTO_INTAKE_STATUSES = [
  'analyzing',
  'awaiting_owner',
  'needs_more_photos',
] as const satisfies readonly PhotoIntakeStatus[];

/** The filters the queue offers. `open` is the default. */
export const PHOTO_INTAKE_FILTERS = ['open', 'sent', 'closed'] as const;
export type PhotoIntakeFilter = (typeof PHOTO_INTAKE_FILTERS)[number];

export const PHOTO_QUOTE_DECISIONS = ['auto_firm', 'owner_glance', 'range_only', 'needs_walkthrough'] as const;
export type PhotoQuoteDecision = (typeof PHOTO_QUOTE_DECISIONS)[number];

export const PHOTO_QUOTE_PRICE_KINDS = ['firm', 'range'] as const;
export type PhotoQuotePriceKind = (typeof PHOTO_QUOTE_PRICE_KINDS)[number];

/** Why a photo is kept out of the crew's view and out of later prompts. */
export const PHOTO_HIDDEN_REASONS = ['person', 'document', 'duplicate', 'irrelevant'] as const;
export type PhotoHiddenReason = (typeof PHOTO_HIDDEN_REASONS)[number];

/**
 * What a single photo shows, as the per-photo read names it. The residential
 * starter library's own room keys, so a photo tagged `full_bathroom` lines up
 * with the library room seeded under that key.
 */
export const PHOTO_ROOM_KINDS = [
  'kitchen',
  'full_bathroom',
  'half_bathroom',
  'bedroom',
  'primary_bedroom',
  'living_room',
  'dining_room',
  'office',
  'hallway',
  'stairway',
  'laundry_room',
  'basement',
  'garage',
  'closet',
  'entry',
  'patio',
  'other_room',
  'not_a_room',
] as const;
export type PhotoRoomKind = (typeof PHOTO_ROOM_KINDS)[number];

/** Things worth calling out that change the work: shown to the owner and the crew. */
export const PHOTO_QUOTE_ITEMS = [
  'pets',
  'oven',
  'fridge',
  'cabinets',
  'blinds',
  'windows',
  'clutter',
  'other',
] as const;
export type PhotoQuoteItem = (typeof PHOTO_QUOTE_ITEMS)[number];

export const PHOTO_FINDING_KINDS = ['room', 'condition', 'sqft', 'item'] as const;
export type PhotoFindingKind = (typeof PHOTO_FINDING_KINDS)[number];

/**
 * Reasons attached to a decision. The first group blocks a price from ever
 * being called safe to send on its own; the second explains why auto-firm did
 * not fire for an otherwise clean result.
 */
export const PHOTO_QUOTE_REASON_CODES = [
  'no_findings',
  'count_mismatch',
  'irrelevant_photos',
  'unread_photos',
  'hidden_photos',
  'unpriced_rooms',
  'commercial',
  'property_unclear',
  'coverage_incomplete',
  'condition_extreme',
  'price_over_cap',
  'monthly_cap_reached',
  'auto_firm_off',
] as const;
export type PhotoQuoteReasonCode = (typeof PHOTO_QUOTE_REASON_CODES)[number];

/** Codes that rule out "safe to send", whatever the score says. */
export const BLOCKING_REASON_CODES: readonly PhotoQuoteReasonCode[] = [
  'no_findings',
  'count_mismatch',
  'irrelevant_photos',
  // A photo the read never saw (unreadable, too large, missing) may show
  // another room or something that changes the work.
  'unread_photos',
  // So may one it kept out because it shows a person or a document.
  'hidden_photos',
  'unpriced_rooms',
  'commercial',
  // The read could not tell whether this is a home at all.
  'property_unclear',
];

/** Why the pipeline did not read the photos. Shown to the owner, never to the customer. */
export const PHOTO_AI_SKIP_REASONS = [
  'plan',
  'ai_off',
  // No owner has accepted the AI notice that names photo quotes.
  'disclosure',
  // The page the customer sent the photos from did not tell them an AI
  // service reads them, so they are never read.
  'notice',
  'unconfigured',
  'allowance',
  'failed',
  'empty_library',
] as const;
export type PhotoAiSkipReason = (typeof PHOTO_AI_SKIP_REASONS)[number];

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

/**
 * Photos one submission may carry, by plan. Free has no upload page at all;
 * a trialing company is treated as Business, like every other entitlement.
 */
export const PHOTO_QUOTE_MAX_PHOTOS: Readonly<Record<PlanId, number>> = {
  free: 0,
  starter: 15,
  pro: 30,
  business: 30,
};

/** The most submissions one email address or phone number may make in a day. */
export const PHOTO_QUOTE_INTAKES_PER_CONTACT_PER_DAY = 3;

/** How long an abandoned upload is kept before it expires (hours). */
export const PHOTO_QUOTE_COLLECTING_TTL_HOURS = 24;

/** Photos are deleted this many days after the intake unless a quote was accepted. */
export const PHOTO_QUOTE_RETENTION_DAYS = 90;

/** How many times the office can ask the customer for more photos. */
export const PHOTO_QUOTE_MAX_MORE_PHOTO_REQUESTS = 2;

/** Photos sent to the model in one triage call. */
export const PHOTO_TRIAGE_BATCH_SIZE = 3;

export const PHOTO_QUOTE_RANGE_WIDTH_MIN = 5;
export const PHOTO_QUOTE_RANGE_WIDTH_MAX = 50;
export const PHOTO_QUOTE_SLA_MINUTES_MIN = 5;
export const PHOTO_QUOTE_SLA_MINUTES_MAX = 24 * 60;
export const PHOTO_QUOTE_TERMS_MAX = 4_000;
export const PHOTO_QUOTE_MESSAGE_MAX = 1_000;

/**
 * What auto-firm would be allowed to send on its own, per plan (spec D2, a
 * decision still open). Not used to send anything in this version; the
 * decision function takes the limit as an input so the rule is testable now.
 */
export const PHOTO_QUOTE_AUTO_FIRM_LIMITS: Readonly<Partial<Record<PlanId, { maxCents: number; perMonth: number }>>> = {
  pro: { maxCents: 40_000, perMonth: 40 },
  business: { maxCents: 100_000, perMonth: 200 },
};

// ---------------------------------------------------------------------------
// Settings
// ---------------------------------------------------------------------------

export const PHOTO_QUOTE_RISK_PRESETS = ['careful', 'balanced', 'bold'] as const;
export type PhotoQuoteRiskPreset = (typeof PHOTO_QUOTE_RISK_PRESETS)[number];

export interface PhotoQuoteThresholds {
  /** At or below this a clean result is safe to send as a firm price. */
  autoFirmMaxScore: number;
  /** At or below this a person takes a quick look; above it, a range or a walkthrough. */
  glanceMaxScore: number;
}

export const PHOTO_QUOTE_THRESHOLDS: Readonly<Record<PhotoQuoteRiskPreset, PhotoQuoteThresholds>> = {
  careful: { autoFirmMaxScore: 0.15, glanceMaxScore: 0.45 },
  balanced: { autoFirmMaxScore: 0.25, glanceMaxScore: 0.55 },
  bold: { autoFirmMaxScore: 0.35, glanceMaxScore: 0.65 },
};

export interface PhotoQuoteSettings {
  /** The upload page is live and submissions are taken. */
  enabled: boolean;
  riskPreset: PhotoQuoteRiskPreset;
  /** A range is the engine's price plus and minus this percentage. */
  rangeWidthPct: number;
  requireKitchen: boolean;
  requireEachBathroom: boolean;
  requireEachBedroom: boolean;
  /** The company's own firm-price terms, or null for the built-in wording. */
  terms: string | null;
  /** Minutes a customer waits during working hours before the clock runs out. */
  slaMinutes: number;
}

export const DEFAULT_PHOTO_QUOTE_SETTINGS: PhotoQuoteSettings = {
  enabled: true,
  riskPreset: 'balanced',
  rangeWidthPct: 15,
  requireKitchen: true,
  requireEachBathroom: true,
  requireEachBedroom: false,
  terms: null,
  slaMinutes: 30,
};

/**
 * The built-in firm-price terms (spec D1: the wording still needs a lawyer's
 * read before it is relied on; the company can replace it in Settings).
 */
export function defaultPhotoQuoteTerms(language: SupportedLanguage): string {
  return language === 'es'
    ? 'Este precio firme se basa en las fotos que nos envió. Lo respetamos si la casa coincide con las fotos. ' +
        'Podemos revisarlo antes de empezar si hay habitaciones o áreas que no aparecen en las fotos, ' +
        'si el estado es muy distinto al que muestran, o si se agregan tareas que no están en este presupuesto.'
    : 'This firm price is based on the photos you sent. We honor it as long as the home matches the photos. ' +
        'We may revisit it before we start if there are rooms or areas the photos did not show, ' +
        'if the condition is very different from what they show, or if work is added that this quote does not list.';
}

/** The terms a firm quote goes out with: the company's own, or the built-in wording. */
export function photoQuoteTerms(settings: Pick<PhotoQuoteSettings, 'terms'>, language: SupportedLanguage): string {
  const own = settings.terms?.trim();
  return own ? own : defaultPhotoQuoteTerms(language);
}

// ---------------------------------------------------------------------------
// Coverage
// ---------------------------------------------------------------------------

/** What the company requires a customer to have photographed. */
export type CoverageRequirement = 'kitchen' | 'bathroom_each' | 'bedroom_each';

export function requiredCoverage(
  settings: Pick<PhotoQuoteSettings, 'requireKitchen' | 'requireEachBathroom' | 'requireEachBedroom'>,
): CoverageRequirement[] {
  return [
    ...(settings.requireKitchen ? (['kitchen'] as const) : []),
    ...(settings.requireEachBathroom ? (['bathroom_each'] as const) : []),
    ...(settings.requireEachBedroom ? (['bedroom_each'] as const) : []),
  ];
}

export interface RoomTally {
  /** How many there are: the larger of what the customer said and what was found. */
  expected: number;
  /** How many of them appear in the photos. */
  photographed: number;
}

export interface CoverageInput {
  requirements: readonly CoverageRequirement[];
  kitchenPhotographed: boolean;
  bathrooms: RoomTally;
  bedrooms: RoomTally;
}

export type CoverageGap =
  { kind: 'kitchen' } | { kind: 'bathroom'; missing: number } | { kind: 'bedroom'; missing: number };

export interface CoverageResult {
  requirements: CoverageRequirement[];
  missing: CoverageGap[];
  complete: boolean;
  /** Share of the required rooms that are not in the photos, 0 to 1. */
  gapRatio: number;
}

/**
 * Deterministic, from counts the pipeline has already settled. Each room a
 * requirement covers is one unit of the ratio: a house with two bathrooms and
 * a kitchen, all required, needs three; one bathroom unseen is a third missing.
 */
export function checkPhotoCoverage(input: CoverageInput): CoverageResult {
  let required = 0;
  let missingUnits = 0;
  const missing: CoverageGap[] = [];

  for (const requirement of input.requirements) {
    if (requirement === 'kitchen') {
      required += 1;
      if (!input.kitchenPhotographed) {
        missingUnits += 1;
        missing.push({ kind: 'kitchen' });
      }
      continue;
    }
    const tally = requirement === 'bathroom_each' ? input.bathrooms : input.bedrooms;
    const expected = Math.max(0, Math.floor(tally.expected));
    const photographed = Math.min(expected, Math.max(0, Math.floor(tally.photographed)));
    required += expected;
    if (photographed < expected) {
      missingUnits += expected - photographed;
      missing.push({
        kind: requirement === 'bathroom_each' ? 'bathroom' : 'bedroom',
        missing: expected - photographed,
      });
    }
  }

  return {
    requirements: [...input.requirements],
    missing,
    complete: missing.length === 0,
    gapRatio: required === 0 ? 0 : roundTo(missingUnits / required, 3),
  };
}

/** Which coverage requirement a library room counts towards, if any. */
export function coverageKindOfRoom(room: {
  seedKey?: string | null;
  categoryCode?: string | null;
  name: string;
}): 'kitchen' | 'bathroom' | 'bedroom' | null {
  const seed = room.seedKey ?? '';
  if (seed === 'kitchen') return 'kitchen';
  if (seed === 'full_bathroom' || seed === 'half_bathroom') return 'bathroom';
  if (seed === 'bedroom' || seed === 'primary_bedroom') return 'bedroom';
  if (room.categoryCode === 'kitchens') return 'kitchen';
  if (room.categoryCode === 'restrooms') return 'bathroom';
  if (room.categoryCode === 'bedrooms') return 'bedroom';
  const name = room.name.toLowerCase();
  if (/\b(kitchen|cocina)\b/.test(name)) return 'kitchen';
  if (/\b(bath|bathroom|restroom|powder room|baño|aseo)\b/.test(name)) return 'bathroom';
  if (/\b(bed ?room|recámara|recamara|dormitorio|habitación)\b/.test(name)) return 'bedroom';
  return null;
}

/** The coverage kind a per-photo room tag counts towards. */
export function coverageKindOfPhoto(kind: PhotoRoomKind): 'kitchen' | 'bathroom' | 'bedroom' | null {
  if (kind === 'kitchen') return 'kitchen';
  if (kind === 'full_bathroom' || kind === 'half_bathroom') return 'bathroom';
  if (kind === 'bedroom' || kind === 'primary_bedroom') return 'bedroom';
  return null;
}

// ---------------------------------------------------------------------------
// Risk (versioned; the version is stored on the intake beside the score)
// ---------------------------------------------------------------------------

export const PHOTO_QUOTE_RISK_MODEL_VERSION = 'v1';

/** The v1 weights. They sum to 1, so a score is already on 0..1 before the clamp. */
export const PHOTO_QUOTE_RISK_WEIGHTS = {
  confidence: 0.3,
  coverage: 0.2,
  condition: 0.15,
  sqftBand: 0.1,
  price: 0.1,
  overrun: 0.1,
  firstTime: 0.05,
} as const;
export type PhotoQuoteRiskComponent = keyof typeof PHOTO_QUOTE_RISK_WEIGHTS;

/** How much harder the job gets with condition; average is the spec's "normal". */
export const CONDITION_SEVERITY: Readonly<Record<RoomConditionCode, number>> = {
  light: 0,
  average: 0.2,
  heavy: 0.6,
  extreme: 1,
};

export interface PhotoQuoteRiskInput {
  /** Mean confidence of the findings, 0 to 1; null when there are none. */
  meanConfidence: number | null;
  /** From the coverage check. */
  coverageGapRatio: number;
  /** The overall condition; null when nothing said. */
  conditionCode: RoomConditionCode | null;
  /** The square-foot estimate as a band; null when there is none. */
  squareFeet: { low: number; high: number } | null;
  /** The engine's price for these findings. */
  priceCents: number;
  /** The company's median quote; null when it has too few to say. */
  companyMedianCents: number | null;
  /** Share of similar jobs that ran over; null until there is a history (v2). */
  historicalOverrunRate: number | null;
  /** Nobody with this email or phone has done business with the company. */
  firstTimeCustomer: boolean;
}

export interface PhotoQuoteRisk {
  /** 0 (safe) to 1 (risky), three decimals. */
  score: number;
  /** Each component before weighting, 0 to 1, so the review screen can explain the score. */
  components: Record<PhotoQuoteRiskComponent, number>;
  version: typeof PHOTO_QUOTE_RISK_MODEL_VERSION;
}

/**
 * The v1 risk score: a weighted sum of seven components, clamped to 0..1.
 * Pure, so the same findings always give the same score, and the score can
 * be recomputed the moment the owner edits a finding.
 */
export function scorePhotoQuoteRisk(input: PhotoQuoteRiskInput): PhotoQuoteRisk {
  const components: Record<PhotoQuoteRiskComponent, number> = {
    confidence: input.meanConfidence === null ? 1 : clamp01(1 - input.meanConfidence),
    coverage: clamp01(input.coverageGapRatio),
    condition: input.conditionCode === null ? CONDITION_SEVERITY.average : CONDITION_SEVERITY[input.conditionCode],
    sqftBand: squareFeetBandWidth(input.squareFeet),
    price: priceAboveMedian(input.priceCents, input.companyMedianCents),
    overrun: input.historicalOverrunRate === null ? 0 : clamp01(input.historicalOverrunRate),
    firstTime: input.firstTimeCustomer ? 1 : 0,
  };
  let sum = 0;
  for (const key of Object.keys(PHOTO_QUOTE_RISK_WEIGHTS) as PhotoQuoteRiskComponent[]) {
    sum += PHOTO_QUOTE_RISK_WEIGHTS[key] * components[key];
  }
  return { score: roundTo(clamp01(sum), 3), components, version: PHOTO_QUOTE_RISK_MODEL_VERSION };
}

/** A band as wide as its own midpoint is as uncertain as v1 measures. */
function squareFeetBandWidth(band: { low: number; high: number } | null): number {
  if (!band) return 1;
  const low = Math.max(0, Math.min(band.low, band.high));
  const high = Math.max(band.low, band.high);
  const mid = (low + high) / 2;
  if (mid <= 0) return 1;
  return clamp01((high - low) / mid);
}

/** 0 at or below the median, 1 at three times it, a straight line between. */
function priceAboveMedian(priceCents: number, medianCents: number | null): number {
  if (medianCents === null || medianCents <= 0) return 0;
  const ratio = priceCents / medianCents;
  if (ratio <= 1) return 0;
  return clamp01((ratio - 1) / 2);
}

// ---------------------------------------------------------------------------
// Decision
// ---------------------------------------------------------------------------

export interface PhotoQuoteDecisionInput {
  score: number;
  priceCents: number;
  coverage: Pick<CoverageResult, 'complete' | 'gapRatio'>;
  conditionCode: RoomConditionCode | null;
  /** Codes the pipeline already raised (count mismatch, stock photos, …). */
  reasonCodes: readonly PhotoQuoteReasonCode[];
  thresholds: PhotoQuoteThresholds;
  autoFirm: {
    enabled: boolean;
    maxCents: number;
    /** Auto-firm sends still allowed this month. */
    remainingThisMonth: number;
  };
}

/** Nothing is sent automatically in this version: every decision is taken with this. */
export const PHOTO_QUOTE_AUTO_FIRM_OFF: PhotoQuoteDecisionInput['autoFirm'] = {
  enabled: false,
  maxCents: 0,
  remainingThisMonth: 0,
};

export interface PhotoQuoteDecisionResult {
  decision: PhotoQuoteDecision;
  /** The codes passed in plus every reason auto-firm did not fire. */
  reasonCodes: PhotoQuoteReasonCode[];
}

/**
 * The spec's decision rule, in its order:
 *
 *  1. `auto_firm` only when the score is within the auto-firm threshold AND the
 *     price is within the cap AND coverage is complete AND the month's cap is
 *     not spent AND auto-firm is on AND nothing blocking was raised.
 *  2. `owner_glance` when the score is within the glance threshold.
 *  3. `needs_walkthrough` when the condition is extreme or more than half the
 *     required rooms are missing.
 *  4. `range_only` otherwise — a range the owner sends.
 */
export function decidePhotoQuote(input: PhotoQuoteDecisionInput): PhotoQuoteDecisionResult {
  const reasons = new Set<PhotoQuoteReasonCode>(input.reasonCodes);
  if (!input.coverage.complete) reasons.add('coverage_incomplete');
  if (input.conditionCode === 'extreme') reasons.add('condition_extreme');
  // The caps only mean something while automatic prices are on; with them
  // off, that is the one reason, and an owner is not told about limits that
  // do not apply to them.
  if (input.autoFirm.enabled) {
    if (input.priceCents > input.autoFirm.maxCents) reasons.add('price_over_cap');
    if (input.autoFirm.remainingThisMonth <= 0) reasons.add('monthly_cap_reached');
  } else {
    reasons.add('auto_firm_off');
  }

  const blocked = BLOCKING_REASON_CODES.some((code) => reasons.has(code));
  const autoFirm =
    input.score <= input.thresholds.autoFirmMaxScore &&
    input.priceCents > 0 &&
    input.priceCents <= input.autoFirm.maxCents &&
    input.coverage.complete &&
    input.autoFirm.remainingThisMonth > 0 &&
    input.autoFirm.enabled &&
    !blocked;

  // Blocking codes rule out auto-firm, not a person's look: a room count that
  // disagrees with the customer is exactly what the glance is for.
  let decision: PhotoQuoteDecision;
  if (autoFirm) decision = 'auto_firm';
  else if (input.score <= input.thresholds.glanceMaxScore) decision = 'owner_glance';
  else if (input.conditionCode === 'extreme' || input.coverage.gapRatio > 0.5) decision = 'needs_walkthrough';
  else decision = 'range_only';

  return { decision, reasonCodes: PHOTO_QUOTE_REASON_CODES.filter((code) => reasons.has(code)) };
}

/** The three-color badge: green "Safe to send", amber "Take a look", red otherwise. */
export type PhotoQuoteRiskLevel = 'low' | 'medium' | 'high';

export function photoQuoteRiskLevel(input: {
  score: number;
  thresholds: PhotoQuoteThresholds;
  coverageComplete: boolean;
  reasonCodes: readonly PhotoQuoteReasonCode[];
}): PhotoQuoteRiskLevel {
  const blocked = BLOCKING_REASON_CODES.some((code) => input.reasonCodes.includes(code));
  if (input.score <= input.thresholds.autoFirmMaxScore && input.coverageComplete && !blocked) return 'low';
  if (input.score <= input.thresholds.glanceMaxScore) return 'medium';
  return 'high';
}

// ---------------------------------------------------------------------------
// Price range
// ---------------------------------------------------------------------------

/**
 * The engine's price plus and minus the company's width, rounded outwards to
 * whole units of the currency (stored amounts are hundredths of a major unit),
 * so "$289.75 to $391.25" reads as "$289 to $392" and the real price is always
 * inside it.
 */
export function photoQuoteRange(priceCents: number, widthPct: number): { lowCents: number; highCents: number } {
  const width = Math.min(PHOTO_QUOTE_RANGE_WIDTH_MAX, Math.max(PHOTO_QUOTE_RANGE_WIDTH_MIN, widthPct));
  const price = Math.max(0, Math.round(priceCents));
  const low = Math.max(0, Math.floor((price * (100 - width)) / 100 / 100) * 100);
  const high = Math.ceil((price * (100 + width)) / 100 / 100) * 100;
  return { lowCents: Math.min(low, price), highCents: Math.max(high, price) };
}

// ---------------------------------------------------------------------------
// The clock
// ---------------------------------------------------------------------------

export interface PhotoQuoteSla {
  /** When the customer should have a price by. */
  dueAt: string;
  /** The submission arrived while the company was closed. */
  outOfHours: boolean;
  /** When the company next opens, when it was closed; else null. */
  opensAt: string | null;
}

/**
 * During working hours the clock starts now; outside them it starts at the
 * next opening, and the customer is told when that is. A company with no
 * working window at all (no days, or a zero-length day) is treated as always
 * open rather than never.
 */
export function photoQuoteSla(input: {
  now: Date;
  timezone: string;
  hours: WorkingHours;
  slaMinutes: number;
}): PhotoQuoteSla {
  const slaMs = Math.max(1, input.slaMinutes) * 60_000;
  const nowMs = input.now.getTime();
  const openNow = (): PhotoQuoteSla => ({
    dueAt: new Date(nowMs + slaMs).toISOString(),
    outOfHours: false,
    opensAt: null,
  });

  const { open, close } = windowMinutes(input.hours);
  if (input.hours.days.length === 0 || close <= open) return openNow();

  const local = localClock(input.now, input.timezone);
  const days = new Set(input.hours.days);
  const today = dayOfWeekOf(local.date);
  if (days.has(today) && local.minutes >= open && local.minutes < close) return openNow();
  // Last night's window, for a company whose day crosses midnight.
  const yesterday = addDaysToDate(local.date, -1);
  if (close > 24 * 60 && days.has(dayOfWeekOf(yesterday)) && local.minutes + 24 * 60 < close) return openNow();

  for (let offset = 0; offset <= 7; offset += 1) {
    const date = addDaysToDate(local.date, offset);
    if (!days.has(dayOfWeekOf(date))) continue;
    const opensAt = localDateTimeToUtc(date, input.hours.startLocal, input.timezone);
    const opensMs = Date.parse(opensAt);
    if (opensMs > nowMs) {
      return { dueAt: new Date(opensMs + slaMs).toISOString(), outOfHours: true, opensAt };
    }
  }
  return openNow();
}

/** The wall-clock date and minutes past midnight of an instant in a zone. */
export function localClock(instant: Date, timezone: string): { date: string; minutes: number } {
  try {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      hourCycle: 'h23',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).formatToParts(instant);
    const at = (type: string): string => parts.find((part) => part.type === type)?.value ?? '';
    const hour = Number(at('hour')) % 24;
    return { date: `${at('year')}-${at('month')}-${at('day')}`, minutes: hour * 60 + Number(at('minute')) };
  } catch {
    const iso = instant.toISOString();
    return { date: iso.slice(0, 10), minutes: instant.getUTCHours() * 60 + instant.getUTCMinutes() };
  }
}

// ---------------------------------------------------------------------------
// Findings: what one claim about the home may say
// ---------------------------------------------------------------------------

export const MAX_ROOM_FINDING_COUNT = 30;
export const MAX_SQUARE_FEET = 50_000;

export const roomFindingValueSchema = z.object({
  roomTemplateId: z.string().min(1).max(255),
  /** The library room's name when the finding was made, for display. */
  name: z.string().trim().min(1).max(160),
  /** How many of these rooms the home has. Zero removes the room from the price. */
  count: z.number().int().min(0).max(MAX_ROOM_FINDING_COUNT),
  /** How many of them appear in the photos. */
  photographedCount: z.number().int().min(0).max(MAX_ROOM_FINDING_COUNT),
  sizeCode: z.enum(ROOM_SIZE_CODES as unknown as [RoomSizeCode, ...RoomSizeCode[]]),
  conditionCode: z.enum(ROOM_CONDITION_CODES as unknown as [RoomConditionCode, ...RoomConditionCode[]]),
  /** Extra library tasks the photos call for, on top of the room's defaults. */
  serviceTemplateIds: z.array(z.string().min(1).max(255)).max(50),
});
export type RoomFindingValue = z.infer<typeof roomFindingValueSchema>;

export const conditionFindingValueSchema = z.object({
  conditionCode: z.enum(ROOM_CONDITION_CODES as unknown as [RoomConditionCode, ...RoomConditionCode[]]),
});
export type ConditionFindingValue = z.infer<typeof conditionFindingValueSchema>;

export const sqftFindingValueSchema = z
  .object({
    low: z.number().int().min(0).max(MAX_SQUARE_FEET),
    high: z.number().int().min(0).max(MAX_SQUARE_FEET),
  })
  .refine((band) => band.high >= band.low, { message: 'The upper estimate must be at least the lower one.' });
export type SqftFindingValue = z.infer<typeof sqftFindingValueSchema>;

export const itemFindingValueSchema = z.object({
  item: z.enum(PHOTO_QUOTE_ITEMS),
  note: z.string().trim().max(240),
  /** Taken off on review: the photos got it wrong. Kept, so it can be put back. */
  dismissed: z.boolean().optional(),
});
export type ItemFindingValue = z.infer<typeof itemFindingValueSchema>;

export type PhotoFindingValue =
  | { kind: 'room'; value: RoomFindingValue }
  | { kind: 'condition'; value: ConditionFindingValue }
  | { kind: 'sqft'; value: SqftFindingValue }
  | { kind: 'item'; value: ItemFindingValue };

/** Validates a stored or edited value for its kind; null when it does not fit. */
export function parseFindingValue(kind: PhotoFindingKind, raw: unknown): PhotoFindingValue | null {
  switch (kind) {
    case 'room': {
      const parsed = roomFindingValueSchema.safeParse(raw);
      return parsed.success ? { kind, value: parsed.data } : null;
    }
    case 'condition': {
      const parsed = conditionFindingValueSchema.safeParse(raw);
      return parsed.success ? { kind, value: parsed.data } : null;
    }
    case 'sqft': {
      const parsed = sqftFindingValueSchema.safeParse(raw);
      return parsed.success ? { kind, value: parsed.data } : null;
    }
    case 'item': {
      const parsed = itemFindingValueSchema.safeParse(raw);
      return parsed.success ? { kind, value: parsed.data } : null;
    }
  }
}

/** One photo's read, as stored on the photo row. */
export const photoTagsSchema = z.object({
  roomKind: z.enum(PHOTO_ROOM_KINDS),
  descriptor: z.string().max(160),
  conditionCode: z.enum(ROOM_CONDITION_CODES as unknown as [RoomConditionCode, ...RoomConditionCode[]]),
  conditionSignals: z.array(z.string().max(120)).max(8),
  items: z.array(z.enum(PHOTO_QUOTE_ITEMS)).max(8),
  containsPerson: z.boolean(),
  containsDocument: z.boolean(),
  relevant: z.boolean(),
  screenshotOrStock: z.boolean(),
  /** The position of an earlier photo this one repeats, or null. */
  duplicateOfPosition: z.number().int().min(1).nullable(),
});
export type PhotoTags = z.infer<typeof photoTagsSchema>;

/** Why a tagged photo should be hidden; privacy first, then noise. */
export function hiddenReasonFor(tags: PhotoTags): PhotoHiddenReason | null {
  if (tags.containsPerson) return 'person';
  if (tags.containsDocument) return 'document';
  if (!tags.relevant || tags.screenshotOrStock || tags.roomKind === 'not_a_room') return 'irrelevant';
  if (tags.duplicateOfPosition !== null) return 'duplicate';
  return null;
}

// ---------------------------------------------------------------------------
// What the customer is told
// ---------------------------------------------------------------------------

/** The customer's view of the status: no internal states, never a price before it is sent. */
export type PhotoIntakeCustomerStatus = 'collecting' | 'reviewing' | 'more_photos' | 'sent' | 'closed';

export function customerStatusFor(status: PhotoIntakeStatus): PhotoIntakeCustomerStatus {
  switch (status) {
    case 'collecting':
      return 'collecting';
    case 'analyzing':
    case 'awaiting_owner':
      return 'reviewing';
    case 'needs_more_photos':
      return 'more_photos';
    case 'sent':
      return 'sent';
    case 'closed':
    case 'expired':
      return 'closed';
  }
}

// ---------------------------------------------------------------------------
// Small helpers
// ---------------------------------------------------------------------------

function clamp01(value: number): number {
  if (!Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

function roundTo(value: number, places: number): number {
  const factor = 10 ** places;
  return Math.round(value * factor) / factor;
}

export function isPhotoIntakeStatus(value: unknown): value is PhotoIntakeStatus {
  return typeof value === 'string' && (PHOTO_INTAKE_STATUSES as readonly string[]).includes(value);
}
